<template>
  <div
    class="frame-renderer"
    ref="container"
    :style="containerStyle"
    @mousemove="onMouseMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
  >
    <transition name="fade" @after-enter="canvasTransitionComplete = true">
      <div
        v-show="isCanvasReady"
        class="frame-renderer__canvas-wrapper"
        :class="{ invisible: hideCanvas }"
      >
        <canvas ref="canvas" width="400" height="300"></canvas>
      </div>
    </transition>

    <!-- Dimension / hover tooltip overlay -->
    <div
      v-if="tooltipVisible"
      class="frame-renderer__tooltip"
      :style="tooltipStyle"
    >
      {{ tooltipText }}
    </div>

    <!-- Dimension annotations (HTML overlay positioned via 3D projection) -->
    <div v-if="showDimensions" class="frame-renderer__dimensions">
      <div
        v-if="widthLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--width"
        :style="widthLabelStyle"
      >
        {{ widthLabel }}
      </div>
      <div
        v-if="heightLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--height"
        :style="heightLabelStyle"
      >
        {{ heightLabel }}
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="frame-renderer__loading">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Thumbnail overlay for seamless loading -->
    <div
      v-if="showTransitionOverlay"
      class="centered"
      :style="thumbnailOverlayStyle"
    >
      <img
        :src="thumbnailUrl"
        alt="Loading preview..."
        class="frame-renderer__thumbnail"
        :style="thumbnailImageStyle"
        @load="onThumbnailImageLoad"
      />
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { debounce } from 'lodash'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js'
import {
  loadTexture,
  loadFrameTextures,
  createArtworkPlane,
  createMiteredFrame,
  createHangHardware,
  createStretcherBars,
  createSubstrateBackPanel,
  updateMiteredRailGeometry,
  updateBackLegGeometry,
  createMatBorder,
  createMultiMatBorder,
  createResizeHandles,
  createDimensionLines,
  computeOuterDimensions,
  applyArtworkTextureCropWindow,
  getCropFromScale,
  inches,
} from '@/utils/frameGeometry.js'
import {
  setupShadowRenderer,
  setupPCSS,
  setupSceneEnvironment,
  createWallPlane,
  createSingleFrameLighting,
  createTelephotoCamera,
  computeFitDistance,
  WALL_WIDTH,
  WALL_HEIGHT,
  EYE_LEVEL,
  CEILING_HEIGHT,
} from '@/utils/sceneLighting.js'
import {
  CAMERA_MODE_STANDARD,
  CAMERA_MODE_FREE,
  AUTO_FRAMING_PIECE_PADDING,
  AUTO_FRAMING_PRESETS,
  selectAutoFramingMode,
  shouldPromoteCameraMode,
  FRAMING_PRESET_BY_MODE,
  autoFramingPresetDistance,
} from '@/utils/designStudioScale.js'

const STANDARD_REFERENCE_VIEWPORT_HEIGHT = 900
const CANONICAL_RENDER_ASPECT = 16 / 10
/**
 * When exporting a thumbnail, fit the full outer frame in the frustum with minimal extra slack.
 * Keep this at ~1 so we pull back only as far as required—larger values increase Z and exaggerate
 * perspective change vs the live camera.
 */
const THUMBNAIL_EXPORT_OUTER_PADDING = 1.01

export default {
  name: 'FrameRenderer',

  props: {
    /** Substrate name (e.g. 'Print', 'Canvas') - affects backing paper, stretchers */
    substrate: {
      type: String,
      default: '',
    },
    /** Artwork width in inches */
    artWidth: {
      type: [Number, String],
      default: 16,
    },
    /** Artwork height in inches */
    artHeight: {
      type: [Number, String],
      default: 20,
    },
    artworkScale: {
      type: [Number, String],
      default: 1,
    },
    artworkOffsetLeft: {
      type: [Number, String],
      default: 0,
    },
    artworkOffsetTop: {
      type: [Number, String],
      default: 0,
    },
    /** Deterministic target finished width in inches for fit/sizing. */
    targetWidth: {
      type: [Number, String],
      default: null,
    },
    /** Deterministic target finished height in inches for fit/sizing. */
    targetHeight: {
      type: [Number, String],
      default: null,
    },
    /** When true, render a built-in 1:1 checker texture for debugging aspect/UVs. */
    debugChecker: {
      type: Boolean,
      default: false,
    },
    /** URL of the artwork image */
    artworkImageUrl: {
      type: String,
      default: null,
    },
    /**
     * Frame option extra_data from the available-options API.
     * Uses top_image for texturing. Expected shape: { top_image, width, rabbet, ... }
     * Pass null for no frame.
     */
    frameData: {
      type: Object,
      default: null,
    },
    /**
     * Mat option extra_data.
     * Expected shape: { color, ... }
     * Pass null for no mat.
     */
    matData: {
      type: Object,
      default: null,
    },
    /** Multi-mat: array of { color } for Mat 1, Mat 2, Mat 3 (outer to inner) */
    matDataArray: {
      type: Array,
      default: null,
    },
    /** Mat border width in inches. Object { top, bottom, left, right } or single number. */
    matWidth: {
      type: [Number, String, Object],
      default: 2,
    },
    /** Multi-mat: per-mat widths [{ top, bottom, left, right }, ...] */
    matWidthsArray: {
      type: Array,
      default: null,
    },
    /** Container max width in CSS (e.g. '100%', '600px') */
    maxWidth: {
      type: String,
      default: '100%',
    },
    /** Container max height in CSS */
    maxHeight: {
      type: String,
      default: '500px',
    },
    /** Whether interactive features (hover, resize) are enabled */
    interactive: {
      type: Boolean,
      default: true,
    },
    /** Background color of the scene */
    backgroundColor: {
      type: String,
      default: '#f0f0f0',
    },
    /**
     * Hang option data for back-of-frame hardware visualization.
     * Expected shape: { name: 'wire' | 'security hardware' | ... }
     */
    hangData: {
      type: Object,
      default: null,
    },
    /** Stretcher bar depth in inches (Canvas only). When > 0 and substrate is Canvas, show stretchers. */
    stretcherDepth: {
      type: [Number, String],
      default: 0,
    },
    /** Canvas mount style: "Gallery Wrapped", "Frame", or "Float Frame" */
    canvasMount: {
      type: String,
      default: '',
    },
    /** Extra data from Float Frame option (has width for frame face) */
    floatFrameData: {
      type: Object,
      default: null,
    },
    /** Substrate back panel: { depth, color, roughness, metalness } for Aluminum, Acrylic, etc. */
    substrateBackData: {
      type: Object,
      default: null,
    },
    /** Whether to show a wall backdrop behind the frame */
    wallEnabled: {
      type: Boolean,
      default: true,
    },
    /** Wall color (CSS string or hex number) */
    wallColor: {
      type: [String, Number],
      default: '#ffffff',
    },
    /** URL of a pre-rendered thumbnail to show while loading */
    thumbnailUrl: {
      type: String,
      default: null,
    },
    /** Whether the parent is still fetching configuration or options data */
    awaitingConfig: {
      type: Boolean,
      default: false,
    },
    /** Optional shared-element transition name for thumbnail image */
    thumbnailTransitionName: {
      type: String,
      default: '',
    },
    /** Whether Three.js canvas/rendering should be initialized */
    canvasEnabled: {
      type: Boolean,
      default: true,
    },
    /** Whether canvas layer should be visually hidden */
    hideCanvas: {
      type: Boolean,
      default: false,
    },
    /** Camera mode: standard, wall, or free */
    cameraMode: {
      type: String,
    },
    /**
     * Optional decor asset: a GLB you can place in the scene for scale/context
     * (plant, furniture, full room scene, etc).
     *
     * Scene units are inches; by default we auto-scale the GLB to the desired height (inches)
     * and drop it onto the floor (y=0).
     */
    decorEnabled: {
      type: Boolean,
      default: false,
    },
    decorUrl: {
      type: String,
      default: '/monstera_deliciosa_potted_mid-century_plant.glb',
    },
    decorHeightInches: {
      type: [Number, String],
      default: 48,
    },
    decorOffsetX: {
      type: [Number, String],
      default: -40,
    },
    /**
     * Distance in front of the wall (inches). Wall is behind z=0; positive z moves toward camera.
     */
    decorFromWall: {
      type: [Number, String],
      default: 14,
    },
  },

  data() {
    return {
      loading: false,
      showThumbnail: true,
      hasInitialFit: false,
      canvasTransitionComplete: false,
      tooltipVisible: false,
      tooltipText: '',
      tooltipX: 0,
      tooltipY: 0,
      showDimensions: false,
      widthLabel: '',
      heightLabel: '',
      widthLabelPos: { x: 0, y: 0 },
      heightLabelPos: { x: 0, y: 0 },
      isDragging: false,
      dragHandle: null,
      dragStartPos: null,
      dragStartDims: null,
      dragWidth: null,
      dragHeight: null,
      dragMode: null, // 'resize' | 'source-move' | 'source-scale'
      flipRotation: 0,
      flipAnimating: false,
    }
  },

  /**
   * Three.js objects are stored directly on the instance (not in data())
   * to avoid Vue reactivity overhead and the _-prefix reservation rule.
   */
  created() {
    this.threeRenderer = null
    this.threeScene = null
    this.threeCamera = null
    this.threeRaycaster = null
    this.threeMouse = null
    this.animFrameId = null
    this.artworkMesh = null
    this.frameGroup = null
    this.matGroup = null
    this.stretcherGroup = null
    this.substrateBackMesh = null
    this.handlesGroup = null
    this.dimGroup = null
    this.sceneContentGroup = null
    this.hangHardwareGroup = null
    this.wallMesh = null
    this.sceneLights = []
    this.builtDims = null
    this.hoveredObject = null
    this._textureCache = null // { artworkUrl, artworkTex, frameKey, frameTextures }
    this.pendingDragDims = null
    this.dragRafId = null
    this.lastEmittedDims = null // { width, height } - avoid duplicate emits
    this.pendingSourceTransform = null
    this.sourceDragRafId = null
    this.sourceGhostMesh = null
    this.sourceGhostFadeId = null
    this.flipZOffset = 0 // current Z translation during flip animation

    // Decor GLB (optional)
    this._decorLoader = null
    this._decorObject = null
    this._decorLoadPromise = null
    this._wallZOffset = 2
    this._pointerControls = null
    this._freeLookActive = false
    this._freeLookKeys = new Set()
    this._boundFreeLookKeyDown = this._onFreeLookKeyDown.bind(this)
    this._boundFreeLookKeyUp = this._onFreeLookKeyUp.bind(this)
    this._boundPointerUnlock = this._onPointerUnlock.bind(this)
  },

  computed: {
    containerStyle() {
      return {
        width: '100%',
        maxWidth: this.maxWidth,
        height: this.maxHeight,
        maxHeight: this.maxHeight,
        position: 'relative',
        overflow: 'hidden',
      }
    },
    tooltipStyle() {
      return {
        left: this.tooltipX + 'px',
        top: this.tooltipY + 'px',
      }
    },
    widthLabelStyle() {
      return {
        left: this.widthLabelPos.x + 'px',
        top: this.widthLabelPos.y + 'px',
      }
    },
    heightLabelStyle() {
      return {
        left: this.heightLabelPos.x + 'px',
        top: this.heightLabelPos.y + 'px',
      }
    },
    parsedArtWidth() {
      if (this.dragWidth != null) return this.dragWidth
      return parseFloat(this.artWidth) || 16
    },
    parsedArtHeight() {
      if (this.dragHeight != null) return this.dragHeight
      return parseFloat(this.artHeight) || 20
    },
    parsedArtworkScale() {
      const n = parseFloat(this.artworkScale)
      return Number.isFinite(n) && n >= 1 ? n : 1
    },
    parsedArtworkOffsetLeft() {
      const n = parseFloat(this.artworkOffsetLeft)
      return Number.isFinite(n) ? n : 0
    },
    parsedArtworkOffsetTop() {
      const n = parseFloat(this.artworkOffsetTop)
      return Number.isFinite(n) ? n : 0
    },
    frameWidth() {
      return this.frameData ? parseFloat(this.frameData.width) || 0 : 0
    },
    /** Frame depth: match frame width up to 2", default to 1.5" */
    frameDepth() {
      const w = this.frameWidth
      return w > 0 ? Math.min(2, w) : 1.5
    },
    frameRabbet() {
      return this.frameData ? parseFloat(this.frameData.rabbet) || 0 : 0
    },
    parsedMatWidth() {
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0)
      if (!hasMat) return { top: 0, bottom: 0, left: 0, right: 0 }
      if (typeof this.matWidth === 'object' && this.matWidth !== null) {
        return {
          top: parseFloat(this.matWidth.top) || 0,
          bottom: parseFloat(this.matWidth.bottom) || 0,
          left: parseFloat(this.matWidth.left) || 0,
          right: parseFloat(this.matWidth.right) || 0,
        }
      }
      const w = parseFloat(this.matWidth) || 0
      return { top: w, bottom: w, left: w, right: w }
    },
    /** Max mat width for any side — used for frame offset calculations */
    maxMatWidth() {
      const m = this.parsedMatWidth
      return Math.max(m.top, m.bottom, m.left, m.right)
    },
    hasMatWidth() {
      const m = this.parsedMatWidth
      return m.top > 0 || m.bottom > 0 || m.left > 0 || m.right > 0
    },
    /** Calculates the maximum Z coordinate (closest to camera) of the generated 3D geometry */
    frontZ() {
      const isCanvas = /canvas/i.test(this.substrate || '')
      const isFloatFrame = this.floatFrameData != null && this.floatFrameData.width > 0

      const effectiveFrameWidth = isFloatFrame && this.floatFrameData
        ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
        : this.frameWidth

      const isGalleryWrapped = this.canvasMount?.toLowerCase() === 'gallery wrap'
      const showFrame = !isGalleryWrapped && effectiveFrameWidth > 0

      if (isCanvas) {
        // Canvas front face is always at z=0, frames wrap backwards
        return 0
      }

      if (showFrame) {
        // Standard frames on paper start at z=0 and extrude towards camera by frameDepth
        const frameDepth = Math.min(2, effectiveFrameWidth) || 1.5
        return inches(frameDepth)
      }

      // Paper with mat but no frame: mat sits at z=0 and extrudes outward
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0) || this.hasMatWidth
      if (hasMat) {
        // createMatBorder uses depth = 0.0625 inches
        return inches(0.0625)
      }

      // Plain paper or otherwise
      return 0
    },
    isCanvasReady() {
      if (this.thumbnailUrl) {
        return !this.showThumbnail
      }
      return this.hasInitialFit && !this.awaitingConfig
    },
    showTransitionOverlay() {
      if (!this.thumbnailUrl) return false
      return !this.canvasTransitionComplete || Boolean(this.thumbnailTransitionName)
    },
    thumbnailOverlayStyle() {
      return {
        viewTransitionName: 'none',
      }
    },
    thumbnailImageStyle() {
      const style = {
        viewTransitionName: this.thumbnailTransitionName || 'none',
      }

      const fitSize = this.getThumbnailDisplaySize()
      if (fitSize) {
        style.width = `${fitSize.width}px`
        style.height = `${fitSize.height}px`
        // Fill projected size; if source ratio is slightly off, crop instead of scale-clamping.
        style.objectFit = 'cover'
      }

      return style
    },
  },

  watch: {
    isCanvasReady(val) {
      if (!val) {
        this.canvasTransitionComplete = false
      }
    },
    artWidth() { if (!this.isDragging) this.debouncedRebuild() },
    artHeight() { if (!this.isDragging) this.debouncedRebuild() },
    artworkImageUrl() { this.debouncedRebuild() },
    substrate() { this.debouncedRebuild() },
    canvasMount() { this.debouncedRebuild() },
    floatFrameData: { handler() { this.debouncedRebuild() }, deep: true },
    substrateBackData: { handler() { this.debouncedRebuild() }, deep: true },
    frameData: { handler() { this.debouncedRebuild() }, deep: true },
    matData: { handler() { this.debouncedRebuild() }, deep: true },
    matDataArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidthsArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidth() { this.debouncedRebuild() },
    hangData: { handler() { this.debouncedRebuild() }, deep: true },
    stretcherDepth() { this.debouncedRebuild() },
    backgroundColor() { this.updateBackground() },
    wallEnabled() { this.updateWall() },
    wallColor() { this.updateWall() },
    decorEnabled() { this.updateDecor() },
    decorUrl() { this.updateDecor(true) },
    decorHeightInches() { this.positionDecor() },
    decorOffsetX() { this.positionDecor() },
    decorFromWall() { this.positionDecor() },
    cameraMode(newMode) {
      if (!this.threeRenderer) return
      if (newMode === CAMERA_MODE_FREE) {
        // Enter free-look directly on the user gesture call stack (no nextTick),
        // so Pointer Lock can be granted immediately.
        this.enableFreeLook()
        return
      }
      this.disableFreeLook()
      this.$nextTick(() => {
        if (!this.threeRenderer) return
        if (this.hasInitialFit) {
          this.animateFitCamera()
        } else {
          this.fitCamera()
        }
      })
    },
    thumbnailUrl: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.hasInitialFit = false
          this.canvasTransitionComplete = false
          this.$nextTick(() => {
            if (this.threeRenderer) this.fitCamera()
          })
        }
      }
    },
    awaitingConfig(newVal) {
      if (!newVal) {
        if (this.threeRenderer) {
          this.fitCamera()
        }
        // As soon as the config finishes loading, trigger a rebuild to ensure we drop the overlay
        // and finish the layout with the new final options.
        if (this.debouncedRebuild) {
          this.debouncedRebuild()
        }
      }
    },
    artworkScale() { if (!this.isDragging) this.debouncedRebuild() },
    artworkOffsetLeft() { if (!this.isDragging) this.debouncedRebuild() },
    artworkOffsetTop() { if (!this.isDragging) this.debouncedRebuild() },
    debugChecker() { this.debouncedRebuild() },
    canvasEnabled(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.enableCanvasRendering()
        })
      }
    }
  },

  mounted() {
    this.debouncedRebuild = debounce(() => this.rebuildScene(), 150)
    this.rebuildVersion = 0

    // Wait for layout to settle before initializing Three.js
    this.$nextTick(() => {
      if (this.canvasEnabled) {
        this.enableCanvasRendering()
      }
    })

    // Listen for window resize
    this.boundOnResize = () => this.onResize()
    window.addEventListener('resize', this.boundOnResize)

    this.boundHandleDrag = (e) => this.handleDrag(e)
    this.boundEndResizeDrag = () => this.endResizeDrag()
  },

  beforeDestroy() {
    if (this.boundHandleDrag) {
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
    }
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId)
      this.dragRafId = null
    }
    // Cancel pending debounced rebuilds
    if (this.debouncedRebuild && this.debouncedRebuild.cancel) {
      this.debouncedRebuild.cancel()
    }
    this.stopRenderLoop()
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize)
    }
    if (this.wallMesh) {
      if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
      if (this.wallMesh.material) this.wallMesh.material.dispose()
      this.wallMesh = null
    }
    this._removeDecor()
    this.sceneLights = []
    if (this.threeRenderer) {
      this.threeRenderer.dispose()
      this.threeRenderer.forceContextLoss()
      this.threeRenderer = null
    }
  },

  methods: {
    _selectAutoFramingMode(sceneW, sceneH) {
      const containerMetrics = this.getContainerMetrics()
      if (!containerMetrics) return CAMERA_MODE_STANDARD

      const fov = this.threeCamera?.fov || 75
      return selectAutoFramingMode({
        contentW: sceneW,
        contentH: sceneH,
        viewportW: containerMetrics.containerW,
        viewportH: containerMetrics.containerH,
        fov,
        piecePadding: AUTO_FRAMING_PIECE_PADDING,
        baselineViewportHeight: STANDARD_REFERENCE_VIEWPORT_HEIGHT,
      })
    },
    // ------------------------------------------------------------------
    // Optional decor asset (GLB)
    // ------------------------------------------------------------------

    _disposeObject3D(root) {
      if (!root) return
      root.traverse((obj) => {
        if (!obj) return
        if (obj.geometry) obj.geometry.dispose()
        const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : []
        mats.forEach((m) => {
          if (!m) return
          ;['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'alphaMap'].forEach((k) => {
            const tex = m[k]
            if (tex && tex.dispose) tex.dispose()
          })
          if (m.dispose) m.dispose()
        })
      })
    },

    _removeDecor() {
      if (this._decorObject && this._decorObject.parent) {
        this._decorObject.parent.remove(this._decorObject)
      }
      this._disposeObject3D(this._decorObject)
      this._decorObject = null
      this._decorLoadPromise = null
    },

    _getDecorElements() {
      const url = String(this.decorUrl || '').trim()
      if (!url) return []

      const fromWall = parseFloat(this.decorFromWall) || 14
      return [
        {
          id: 'plant',
          url,
          heightInches: parseFloat(this.decorHeightInches) || 48,
          position: {
            x: (parseFloat(this.decorOffsetX) || 0) + 6,
            z: (-this._wallZOffset) + fromWall + 6,
          },
          animation: {
            duration: 2000,
            fromDeltaX: -24,
            fromDeltaZ: 76,
          },
        },
      ]
    },

    _getPrimaryDecorElement() {
      return this._getDecorElements()[0] || null
    },

    _startDecorEntranceAnimation() {
      const obj = this._decorObject
      const config = this._getPrimaryDecorElement()
      if (!obj || !config) return

      const animation = config.animation || {}
      const toX = config.position?.x || 0
      const toZ = config.position?.z || 0
      const fromX = toX + (animation.fromDeltaX || 0)
      const fromZ = toZ + (animation.fromDeltaZ || 0)

      obj.userData.decorAnimation = {
        startTime: performance.now(),
        duration: Math.max(1, animation.duration || 2000),
        fromX,
        fromZ,
        toX,
        toZ,
      }
      obj.position.x = fromX
      obj.position.z = fromZ
    },

    _updateDecorEntranceAnimation(now) {
      const obj = this._decorObject
      const anim = obj?.userData?.decorAnimation
      if (!obj || !anim) return

      const elapsed = now - anim.startTime
      const duration = Math.max(1, anim.duration)
      const t = Math.min(1, elapsed / duration)
      const eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)

      obj.position.x = anim.fromX + ((anim.toX - anim.fromX) * eased)
      obj.position.z = anim.fromZ + ((anim.toZ - anim.fromZ) * eased)

      if (t >= 1) {
        obj.position.x = anim.toX
        obj.position.z = anim.toZ
        delete obj.userData.decorAnimation
      }
    },

    async _ensureDecorLoaded(forceReload = false) {
      if (!this.threeScene) return null
      if (!this.decorEnabled) return null

      if (forceReload) {
        this._removeDecor()
      }
      if (this._decorObject) return this._decorObject
      if (this._decorLoadPromise) return this._decorLoadPromise

      const decorConfig = this._getPrimaryDecorElement()
      if (!decorConfig) return null
      if (!this._decorLoader) this._decorLoader = new GLTFLoader()
      const url = decorConfig.url

      this._decorLoadPromise = new Promise((resolve) => {
        this._decorLoader.load(
          url,
          (gltf) => resolve(gltf?.scene || null),
          undefined,
          (err) => {
            console.warn('FrameRenderer: decor failed to load', err)
            resolve(null)
          }
        )
      })

      const obj = await this._decorLoadPromise
      this._decorLoadPromise = null
      if (!obj || !this.threeScene || !this.decorEnabled) return null

      // Normalize + scale to target height (inches).
      obj.userData = { ...(obj.userData || {}), type: 'decor', decorId: decorConfig.id }
      obj.traverse((n) => {
        if (n.isMesh) {
          n.castShadow = true
          n.receiveShadow = true
        }
      })

      const box = new THREE.Box3().setFromObject(obj)
      const size = box.getSize(new THREE.Vector3())
      const desiredH = inches(decorConfig.heightInches)
      if (size.y > 0 && desiredH > 0) {
        const s = desiredH / size.y
        obj.scale.multiplyScalar(s)
      }

      // Drop to floor (y=0)
      const box2 = new THREE.Box3().setFromObject(obj)
      obj.position.y += -box2.min.y

      this._decorObject = obj
      this.threeScene.add(obj)
      this.positionDecor()
      this._startDecorEntranceAnimation()
      return obj
    },

    async updateDecor(forceReload = false) {
      if (!this.threeScene) return
      if (!this.decorEnabled) {
        this._removeDecor()
        return
      }
      await this._ensureDecorLoaded(forceReload)
    },

    positionDecor() {
      const obj = this._decorObject
      const config = this._getPrimaryDecorElement()
      if (!this.threeScene || !obj || !config) return

      const x = config.position?.x || 0
      const z = config.position?.z || 0
      const anim = obj.userData?.decorAnimation
      if (anim) {
        anim.toX = x
        anim.toZ = z
      } else {
        obj.position.x = x
      }
      obj.position.z = z
    },
    // ------------------------------------------------------------------
    // Source image drag helpers (shared by move, scale, and resize)
    // ------------------------------------------------------------------

    _beginDrag(event, mode, cursor) {
      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      this.isDragging = true
      this.dragMode = mode
      this.dragStartPos = { x: event.clientX, y: event.clientY }
      this.dragSourceStart = {
        scale: this.parsedArtworkScale,
        offsetLeft: this.parsedArtworkOffsetLeft,
        offsetTop: this.parsedArtworkOffsetTop,
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
      }
      document.addEventListener('mousemove', this.boundHandleDrag)
      document.addEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = cursor
      document.body.style.userSelect = 'none'
      event.preventDefault()
    },

    _getImageAspect() {
      const img = this.artworkMesh?.material?.map?.image
      if (img) {
        const w = img.width || img.naturalWidth
        const h = img.height || img.naturalHeight
        if (w > 0 && h > 0) return w / h
      }
      return this.parsedArtWidth / this.parsedArtHeight
    },

    _applySourceAndEmit(artW, artH, transform) {
      const imgAspect = this._getImageAspect()
      const cropDims = getCropFromScale(artW, artH, transform.scale, transform.offsetLeft, transform.offsetTop, imgAspect)
      const tex = this.artworkMesh?.material?.map
      if (tex) {
        applyArtworkTextureCropWindow(tex, artW, artH, cropDims.fullWidth, cropDims.fullHeight, cropDims.cropLeft, cropDims.cropTop)
      }
      this._updateSourceGhost(transform)
      this.$emit('source-transform', transform)
    },

    handleSourceMoveDrag(dxInches, dyInches) {
      const s = this.dragSourceStart
      if (!s) return
      const imgAspect = this._getImageAspect()
      const baseCoverW = Math.max(s.artWidth, s.artHeight * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      const uw = baseCoverW * s.scale
      const uh = baseCoverH * s.scale
      const maxOL = (uw - s.artWidth) / 2
      const maxOT = (uh - s.artHeight) / 2
      const ol = Math.min(Math.max(-maxOL, s.offsetLeft + dxInches), maxOL)
      const ot = Math.min(Math.max(-maxOT, s.offsetTop + dyInches), maxOT)
      const transform = { scale: s.scale, offsetLeft: ol, offsetTop: ot }
      this.pendingSourceTransform = transform
      if (this.sourceDragRafId == null) {
        this.sourceDragRafId = requestAnimationFrame(() => this.applyPendingSourceTransform())
      }
    },

    handleSourceScaleDrag(dxInches) {
      const s = this.dragSourceStart
      if (!s) return
      const imgAspect = this._getImageAspect()
      const baseCoverW = Math.max(s.artWidth, s.artHeight * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      const scaleFromCenterFactor = 4
      const nextScale = Math.max(1, s.scale + (dxInches * scaleFromCenterFactor) / baseCoverW)
      const uw = baseCoverW * nextScale
      const uh = baseCoverH * nextScale
      const maxOL = (uw - s.artWidth) / 2
      const maxOT = (uh - s.artHeight) / 2
      const ol = Math.min(Math.max(-maxOL, s.offsetLeft), maxOL)
      const ot = Math.min(Math.max(-maxOT, s.offsetTop), maxOT)
      const transform = { scale: nextScale, offsetLeft: ol, offsetTop: ot }
      this.pendingSourceTransform = transform
      if (this.sourceDragRafId == null) {
        this.sourceDragRafId = requestAnimationFrame(() => this.applyPendingSourceTransform())
      }
    },

    applyPendingSourceTransform() {
      this.sourceDragRafId = null
      const transform = this.pendingSourceTransform
      this.pendingSourceTransform = null
      const s = this.dragSourceStart
      if (!transform || !s) return
      this._applySourceAndEmit(s.artWidth, s.artHeight, transform)
      this.updateSceneDimensions(this.parsedArtWidth, this.parsedArtHeight, transform)
    },

    adjustSourceForResize(newW, newH) {
      const s = this.dragSourceStart
      if (!s) return null
      const imgAspect = this._getImageAspect()
      const baseCoverW = Math.max(newW, newH * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      const uw = baseCoverW * s.scale
      const uh = baseCoverH * s.scale
      const maxOL = (uw - newW) / 2
      const maxOT = (uh - newH) / 2
      const ol = Math.min(Math.max(-maxOL, s.offsetLeft), maxOL)
      const ot = Math.min(Math.max(-maxOT, s.offsetTop), maxOT)
      return { scale: s.scale, offsetLeft: ol, offsetTop: ot }
    },

    // ------------------------------------------------------------------
    // Uncropped image ghost overlay (visible during source drag)
    // ------------------------------------------------------------------

    _showSourceGhost() {
      this._hideSourceGhost()

      const tex = this.artworkMesh?.material?.map
      if (!tex) return

      const artW = this.parsedArtWidth
      const artH = this.parsedArtHeight
      const imgAspect = this._getImageAspect()
      const crop = getCropFromScale(
        artW, artH,
        this.parsedArtworkScale,
        this.parsedArtworkOffsetLeft,
        this.parsedArtworkOffsetTop,
        imgAspect
      )

      const ghostTex = tex.clone()
      ghostTex.offset.set(0, 0)
      ghostTex.repeat.set(1, 1)
      ghostTex.needsUpdate = true

      const ghostMat = new THREE.MeshBasicMaterial({
        map: ghostTex,
        transparent: true,
        opacity: 0,
        depthTest: false,
        side: THREE.FrontSide,
      })

      const ghostGeo = new THREE.PlaneGeometry(1, 1)
      const ghostMesh = new THREE.Mesh(ghostGeo, ghostMat)

      ghostMesh.scale.set(inches(crop.fullWidth), inches(crop.fullHeight), 1)
      const ghostX = inches((crop.fullWidth - artW) / 2 - crop.cropLeft)
      const ghostY = inches((artH - crop.fullHeight) / 2 + crop.cropTop)
      ghostMesh.position.set(ghostX, ghostY, 0.15)

      ghostMesh.renderOrder = 100
      ghostMesh.userData = { type: 'source-ghost' }
      ghostMesh.raycast = () => {}

      this.sourceGhostMesh = ghostMesh
      if (this.sceneContentGroup) {
        this.sceneContentGroup.add(ghostMesh)
      }

      const mat = this.sourceGhostMesh.material
      const startTime = performance.now()
      const duration = 400
      const startOpacity = 0
      const endOpacity = 0.25

      if (this.sourceGhostFadeId != null) {
        cancelAnimationFrame(this.sourceGhostFadeId)
      }

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)
        // easeOutQuad
        const eased = 1 - (1 - t) * (1 - t)
        const nextOpacity = startOpacity + (endOpacity - startOpacity) * eased
        if (mat) {
          mat.opacity = nextOpacity
          mat.needsUpdate = true
        }
        if (t < 1 && this.sourceGhostMesh) {
          this.sourceGhostFadeId = requestAnimationFrame(animate)
        } else {
          this.sourceGhostFadeId = null
        }
      }
      this.sourceGhostFadeId = requestAnimationFrame(animate)
    },

    _updateSourceGhost(transform) {
      if (!this.sourceGhostMesh) return

      const artW = this.parsedArtWidth
      const artH = this.parsedArtHeight
      const imgAspect = this._getImageAspect()
      const crop = getCropFromScale(
        artW, artH,
        transform.scale, transform.offsetLeft, transform.offsetTop,
        imgAspect
      )

      this.sourceGhostMesh.scale.set(inches(crop.fullWidth), inches(crop.fullHeight), 1)
      const ghostX = inches((crop.fullWidth - artW) / 2 - crop.cropLeft)
      const ghostY = inches((artH - crop.fullHeight) / 2 + crop.cropTop)
      this.sourceGhostMesh.position.set(ghostX, ghostY, 0.15)
    },

    _hideSourceGhost() {
      if (!this.sourceGhostMesh) return

      const mesh = this.sourceGhostMesh
      const mat = mesh.material
      const startOpacity = mat?.opacity ?? 0
      const endOpacity = 0
      const startTime = performance.now()
      const duration = 200

      if (this.sourceGhostFadeId != null) {
        cancelAnimationFrame(this.sourceGhostFadeId)
      }

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)
        // easeInQuad
        const eased = t * t
        const nextOpacity = startOpacity + (endOpacity - startOpacity) * eased
        if (mat) {
          mat.opacity = nextOpacity
          mat.needsUpdate = true
        }

        if (t < 1 && this.sourceGhostMesh === mesh) {
          this.sourceGhostFadeId = requestAnimationFrame(animate)
        } else {
          this.sourceGhostFadeId = null
          if (mesh.parent) {
            mesh.parent.remove(mesh)
          }
          mesh.geometry?.dispose()
          mesh.material?.map?.dispose()
          mesh.material?.dispose()
          if (this.sourceGhostMesh === mesh) {
            this.sourceGhostMesh = null
          }
        }
      }

      this.sourceGhostFadeId = requestAnimationFrame(animate)
    },

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    enableCanvasRendering() {
      if (this.threeRenderer) return
      this.initThree()
      this.rebuildScene()
      this.startRenderLoop()
    },

    initThree() {
      const container = this.$refs.container
      const canvas = this.$refs.canvas
      if (!container || !canvas) return

      const rect = container.getBoundingClientRect()
      const width = Math.max(rect.width, 200)
      const height = Math.max(rect.height, 200)

      // Renderer with soft shadow mapping
      this.threeRenderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: false,
        logarithmicDepthBuffer: true,
      })
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.threeRenderer.setSize(width, height)
      this.threeRenderer.outputColorSpace = THREE.SRGBColorSpace
      setupShadowRenderer(this.threeRenderer)
      setupPCSS()

      // Scene
      this.threeScene = new THREE.Scene()
      this.updateBackground()
      setupSceneEnvironment(this.threeRenderer, this.threeScene)

      // Telephoto perspective camera (narrow FOV for flat, compressed look)
      this.threeCamera = createTelephotoCamera(width, height, 18)

      // Lighting: IBL handles ambient; single directional key + spotlight cast PCSS shadows
      this.sceneLights = createSingleFrameLighting({
        centerY: EYE_LEVEL,
        ceilingHeight: CEILING_HEIGHT,
      })
      this.sceneLights.forEach(l => this.threeScene.add(l))

      // Wall backdrop: 12ft × 8ft gallery wall, frame at eye level
      this.updateWall()
      this.updateDecor()

      // Raycaster for interactions
      this.threeRaycaster = new THREE.Raycaster()
      this.threeMouse = new THREE.Vector2()

      // Pointer lock controls for free-look mode
      this._pointerControls = new PointerLockControls(this.threeCamera, canvas)
    },

    updateBackground() {
      if (this.threeScene) {
        this.threeScene.background = new THREE.Color(this.backgroundColor)
      }
    },

    updateWall() {
      if (!this.threeScene) return
      // Remove existing wall
      if (this.wallMesh) {
        this.threeScene.remove(this.wallMesh)
        if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
        if (this.wallMesh.material) this.wallMesh.material.dispose()
        this.wallMesh = null
      }
      if (this.wallEnabled) {
        const zOffset = this.builtDims?.backDepth != null ? this.builtDims.backDepth + 0.01 : 2
        this._wallZOffset = zOffset
        this.wallMesh = createWallPlane(WALL_WIDTH, WALL_HEIGHT, this.wallColor, zOffset, WALL_HEIGHT / 2)
        this.threeScene.add(this.wallMesh)
      }
      this.positionDecor()
    },

    // ------------------------------------------------------------------
    // Scene building
    // ------------------------------------------------------------------

    async rebuildScene(opts = {}) {
      if (!this.threeScene) return

      const silent = opts.silent === true
      const version = ++this.rebuildVersion
      if (!silent) this.loading = true

      const isCanvas = String(this.substrate || '').toLowerCase() === 'canvas'
      const canvasMount = String(this.canvasMount || '').trim()
      const isGalleryWrapped = isCanvas && /gallery\s*wrapped/i.test(canvasMount)
      const isFloatFrame = isCanvas && /float\s*frame/i.test(canvasMount)

      const effectiveFrameData = isFloatFrame && this.floatFrameData
        ? this.floatFrameData
        : this.frameData
      const frameKey = effectiveFrameData?.top_image || this.frameData?.top_image || ''

      let artworkTex
      let textures

      try {
        // Use cached textures for silent rebuilds (fast path during drag)
        if (silent && this._textureCache) {
          const c = this._textureCache
          if (c.artworkUrl === this.artworkImageUrl && c.frameKey === frameKey) {
            artworkTex = c.artworkTex
            textures = c.frameTextures
          }
        }
        if (!artworkTex) {
          artworkTex = await loadTexture(this.artworkImageUrl)
          if (version !== this.rebuildVersion) return
        }

        // Optional debug path: replace artwork with a built-in 1:1 checkerboard
        // so we can visually confirm aspect/UV correctness independent of the
        // source image.
        if (this.debugChecker) {
          const texImg = artworkTex?.image
          const aspect = (texImg && texImg.width > 0 && texImg.height > 0)
            ? texImg.width / texImg.height
            : this.parsedArtWidth / this.parsedArtHeight
          const base = 512
          const texW = aspect >= 1 ? base : Math.round(base * aspect)
          const texH = aspect >= 1 ? Math.round(base / aspect) : base
          const canvas = document.createElement('canvas')
          canvas.width = texW
          canvas.height = texH
          const ctx = canvas.getContext('2d')
          if (ctx) {
            const cellPx = Math.max(1, Math.round(base / 16))
            const cellsX = Math.ceil(texW / cellPx)
            const cellsY = Math.ceil(texH / cellPx)
            for (let y = 0; y < cellsY; y++) {
              for (let x = 0; x < cellsX; x++) {
                ctx.fillStyle = (x + y) % 2 === 0 ? '#ffffff' : '#000000'
                ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx)
              }
            }
          }
          artworkTex = new THREE.CanvasTexture(canvas)
          artworkTex.anisotropy = 16
          artworkTex.needsUpdate = true
        }
        if (!textures && effectiveFrameData && (parseFloat(effectiveFrameData.width) || parseFloat(this.frameWidth))) {
          textures = await loadFrameTextures(effectiveFrameData)
          if (version !== this.rebuildVersion) return
        }
        if (!textures) textures = { front: null, side: null, top: null }

        this._textureCache = {
          artworkUrl: this.artworkImageUrl,
          artworkTex,
          frameKey,
          frameTextures: textures,
        }

        const mw = isGalleryWrapped ? { top: 0, bottom: 0, left: 0, right: 0 } : this.parsedMatWidth
        const floatFrameGap = isFloatFrame ? (parseFloat(this.floatFrameData?.gap) || 0.25) : 0

        const wfBack = this.substrateBackData
        const isWindowFilm = /window\s*film/i.test(this.substrate || '')
        const artworkOpts = isWindowFilm && wfBack ? {
        side: wfBack.useBackface ? THREE.DoubleSide : THREE.FrontSide,
        opacity: wfBack.opacity ?? 1,
        transparent: (wfBack.opacity ?? 1) < 1,
        perforated: wfBack.perforated ?? false,
      } : {}

        const artW = this.parsedArtWidth
        const artH = this.parsedArtHeight

        const planeW = artW
        const planeH = artH

        const imgAspect = (() => {
          const texImg = artworkTex?.image
          if (texImg) {
            const w = texImg.width || texImg.naturalWidth
            const h = texImg.height || texImg.naturalHeight
            if (w > 0 && h > 0) return w / h
          }
          return artW / artH
        })()
        const cropDims = getCropFromScale(
          artW, artH,
          this.parsedArtworkScale,
          this.parsedArtworkOffsetLeft,
          this.parsedArtworkOffsetTop,
          imgAspect
        )
        const cropOpts = {
          ...artworkOpts,
          fullWidth: cropDims.fullWidth,
          fullHeight: cropDims.fullHeight,
          cropLeft: cropDims.cropLeft,
          cropTop: cropDims.cropTop,
        }
        const newArtworkMesh = createArtworkPlane(
          planeW,
          planeH,
          artworkTex,
          cropOpts
        )
        newArtworkMesh.renderOrder = 1

        let newMatGroup = null
        const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
        const widthsArr = this.matWidthsArray || (this.hasMatWidth ? [mw] : [])
        if (!isGalleryWrapped && matArr.length > 0 && widthsArr.length > 0) {
          if (matArr.length > 1) {
            newMatGroup = createMultiMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              mats: matArr,
              widths: widthsArr,
            })
          } else {
            newMatGroup = createMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              matTop: mw.top,
              matBottom: mw.bottom,
              matLeft: mw.left,
              matRight: mw.right,
              color: (matArr[0]?.color || this.matData?.color || '#FFFFFF'),
            })
          }
        }

        let newFrameGroup = null
        const stretcherD = Number(this.stretcherDepth) || 0
        const effectiveFrameWidth = isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const showFrame = !isGalleryWrapped && effectiveFrameData && effectiveFrameWidth > 0
        if (showFrame) {
          // Canvas frame depth matches stretcher assembly depth (75% of bar width) when stretchers present
          const stretcherActualDepth = stretcherD > 0 ? stretcherD * 0.75 : 0
          const frameDepth = isCanvas && stretcherD > 0
            ? stretcherActualDepth
            : Math.min(2, effectiveFrameWidth)
          const innerW = this.parsedArtWidth + mw.left + mw.right + (isFloatFrame ? 2 * floatFrameGap : 0)
          const innerH = this.parsedArtHeight + mw.top + mw.bottom + (isFloatFrame ? 2 * floatFrameGap : 0)
          newFrameGroup = createMiteredFrame({
            artWidth: innerW,
            artHeight: innerH,
            frameWidth: effectiveFrameWidth,
            rabbet: effectiveFrameData.rabbet != null ? parseFloat(effectiveFrameData.rabbet) : this.frameRabbet,
            depth: frameDepth,
            textures,
            isFloatFrame,
          })
          const offsetX = (mw.right - mw.left) / 2
          const offsetY = (mw.top - mw.bottom) / 2
          // Canvas: align frame front with canvas front (both at z=0); Print: frame front at z=d
          const frameZ = isCanvas ? -inches(frameDepth) : 0
          newFrameGroup.position.set(offsetX, offsetY, frameZ)
        }

        let newSubstrateBackMesh = null
        const isMural = /mural/i.test(this.substrate || '')
        const isWindowFilmOpaque = /window\s*film/i.test(this.substrate || '') && this.substrateBackData?.depth > 0
        if (this.substrateBackData && this.substrateBackData.depth > 0 && (!showFrame || isMural || isWindowFilmOpaque)) {
          const sb = this.substrateBackData
          const border = sb.border || 0
          const subW = this.parsedArtWidth + 2 * border
          const subH = this.parsedArtHeight + 2 * border
          newSubstrateBackMesh = createSubstrateBackPanel(subW, subH, sb)
          const sbOffsetX = inches((mw.right - mw.left) / 2)
          const sbOffsetY = inches((mw.top - mw.bottom) / 2)
          newSubstrateBackMesh.position.set(sbOffsetX, sbOffsetY, 0)
        }

        let newStretcherGroup = null
        const depth = stretcherD
        if (isCanvas && depth > 0) {
          newStretcherGroup = createStretcherBars(
            this.parsedArtWidth,
            this.parsedArtHeight,
            depth
          )
        }

        let newHandlesGroup = null
        let newHangHardwareGroup = null
        const innerW = this.parsedArtWidth + mw.left + mw.right
        const innerH = this.parsedArtHeight + mw.top + mw.bottom
        const hwOffsetX = inches((mw.right - mw.left) / 2)
        const hwOffsetY = inches((mw.top - mw.bottom) / 2)
        if (this.hangData) {
          const hangName = this.hangData.name || this.hangData.value || ''
          // Backmost depth: stretchers (if present), else substrate back, else frame depth for canvas, else default
          const frameD = showFrame ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth)) : 0
          const substrateD = this.substrateBackData?.depth || 0
          const backDepth = isCanvas && stretcherD > 0 ? stretcherD * 0.75
            : (isCanvas && frameD > 0 ? frameD : substrateD > 0 ? substrateD : 0.1)
          // Canvas with stretchers: hardware must stay within stretcher bars (art size). Otherwise use inner (art + mat).
          const hwWidth = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
          const hwHeight = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
          newHangHardwareGroup = createHangHardware({
            innerWidth: hwWidth,
            innerHeight: hwHeight,
            hangType: hangName,
            substrate: this.substrate || '',
            backDepth,
          })
          // Align backing paper with frame opening when mat is asymmetrical
          newHangHardwareGroup.position.set(hwOffsetX, hwOffsetY, 0)
        }

        let newDimGroup = null
        if (this.interactive) {
          newHandlesGroup = createResizeHandles(
            this.parsedArtWidth,
            this.parsedArtHeight
          )

          // Center source transform gizmo: move + scale handles connected by a link.
          const sourceGroup = new THREE.Group()
          sourceGroup.userData = { type: 'source-handles' }

          const awIn = this.parsedArtWidth
          const aw = inches(awIn)
          const handleSize = inches(1) // radius (2" diameter)

          const centerGeo = new THREE.SphereGeometry(handleSize, 18, 18)
          const scaleGeo = new THREE.SphereGeometry(handleSize * 0.8, 16, 16)
          const centerMat = new THREE.MeshStandardMaterial({
            color: 0x00bcd4,
            emissive: 0x00838f,
            emissiveIntensity: 0.4,
            transparent: true,
            opacity: 0,
            depthTest: false,
          })
          const scaleMat = new THREE.MeshStandardMaterial({
            color: 0x8bc34a,
            emissive: 0x558b2f,
            emissiveIntensity: 0.4,
            transparent: true,
            opacity: 0,
            depthTest: false,
          })
          const linkMat = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            transparent: true,
            opacity: 0,
            depthTest: false,
          })

          const centerHandle = new THREE.Mesh(centerGeo, centerMat)
          centerHandle.position.set(0, 0, 0.6)
          centerHandle.userData = {
            type: 'source-handle-move',
            label: 'Move image',
          }
          sourceGroup.add(centerHandle)

          const scaleOffset = aw * 0.25
          const scaleHandle = new THREE.Mesh(scaleGeo, scaleMat)
          scaleHandle.position.set(scaleOffset, 0, 0.6)
          scaleHandle.userData = {
            type: 'source-handle-scale',
            label: 'Scale image',
          }
          sourceGroup.add(scaleHandle)

          const linkLen = Math.abs(scaleOffset)
          const linkGeo = new THREE.BoxGeometry(linkLen, handleSize * 0.25, 0.1)
          const link = new THREE.Mesh(linkGeo, linkMat)
          link.position.set(scaleOffset / 2, 0, 0.55)
          link.userData = {
            type: 'source-handle-link',
          }
          sourceGroup.add(link)

          newHandlesGroup.add(sourceGroup)

          const dimArtW = isFloatFrame ? this.parsedArtWidth + 2 * floatFrameGap : this.parsedArtWidth
          const dimArtH = isFloatFrame ? this.parsedArtHeight + 2 * floatFrameGap : this.parsedArtHeight
          const substrateBorder = this.substrateBackData?.border ?? 0
          const { outerWidth, outerHeight } = computeOuterDimensions({
            artWidth: dimArtW,
            artHeight: dimArtH,
            matTop: mw.top,
            matBottom: mw.bottom,
            matLeft: mw.left,
            matRight: mw.right,
            frameWidth: showFrame ? effectiveFrameWidth : 0,
            rabbet: this.frameRabbet,
            substrateBorder,
          })
          newDimGroup = createDimensionLines(
            this.parsedArtWidth,
            this.parsedArtHeight,
            (showFrame ? effectiveFrameWidth : 0) + this.maxMatWidth,
            outerWidth,
            outerHeight
          )
        }

        // Swap: remove old, add new in one sync block
        const contentParent = this.sceneContentGroup || this.threeScene
        const toRemove = []
        contentParent.traverse((obj) => {
          if (obj !== this.sceneContentGroup && obj.userData?.type) toRemove.push(obj)
        })
        toRemove.forEach((obj) => {
          if (obj.parent) obj.parent.remove(obj)
          if (obj.geometry) obj.geometry.dispose()
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(m => m.dispose())
            } else {
              obj.material.dispose()
            }
          }
        })

        if (!this.sceneContentGroup) {
          this.sceneContentGroup = new THREE.Group()
          this.sceneContentGroup.userData = { type: 'content' }
          this.threeScene.add(this.sceneContentGroup)
        }
        this.sceneContentGroup.scale.set(1, 1, 1)
        this.sceneContentGroup.position.set(0, EYE_LEVEL, 0)

        const frameDepthUsed = showFrame
          ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth))
          : 0
        const hwW = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
        const hwH = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
        const substrateBorder = this.substrateBackData?.border || 0
        this.builtDims = {
          artW: artW,
          artH: artH,
          planeW,
          planeH,
          substrateBorder,
          innerW,
          innerH,
          backPanelBuiltW: innerW + 2,
          backPanelBuiltH: innerH + 2,
          hwBuiltW: hwW,
          hwBuiltH: hwH,
          mw: { ...mw },
          isFloatFrame,
          floatFrameGap,
          isCanvas,
          frameDepthUsed,
          hasStretchers: isCanvas && stretcherD > 0,
          backDepth: (() => {
            const fd = showFrame ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth)) : 0
            const sd = this.substrateBackData?.depth || 0
            return isCanvas && stretcherD > 0 ? stretcherD * 0.75 : (isCanvas && fd > 0 ? fd : sd > 0 ? sd : 0.1)
          })(),
        }

        this.artworkMesh = newArtworkMesh
        this.matGroup = newMatGroup
        this.frameGroup = newFrameGroup
        this.substrateBackMesh = newSubstrateBackMesh
        this.stretcherGroup = newStretcherGroup
        this.hangHardwareGroup = newHangHardwareGroup
        this.handlesGroup = newHandlesGroup
        this.dimGroup = newDimGroup

        this.sceneContentGroup.add(this.artworkMesh)
        if (this.matGroup) this.sceneContentGroup.add(this.matGroup)
        if (this.stretcherGroup) this.sceneContentGroup.add(this.stretcherGroup)
        if (this.substrateBackMesh) this.sceneContentGroup.add(this.substrateBackMesh)
        if (this.frameGroup) this.sceneContentGroup.add(this.frameGroup)
        if (this.hangHardwareGroup) this.sceneContentGroup.add(this.hangHardwareGroup)
        if (this.handlesGroup) this.sceneContentGroup.add(this.handlesGroup)
        if (this.dimGroup) this.sceneContentGroup.add(this.dimGroup)

        // Enable shadows on all meshes except resize/source handles and ghost overlay
        this.sceneContentGroup.traverse((obj) => {
          if (obj.isMesh && obj.userData?.type !== 'resize-handle' && !obj.userData?.type?.startsWith?.('source-')) {
            obj.castShadow = true
            obj.receiveShadow = true
          }
        })

        this.updateSceneDimensions(this.parsedArtWidth, this.parsedArtHeight)
        this.updateWall()

        if (!silent) {
          if (!this.hasInitialFit || this.awaitingConfig) {
            await this.applyAutoFramingForCurrentScene({ instant: true })
            // Wait for Vue to update DOM and Three.js to render a frame
            await new Promise((resolve) => {
              requestAnimationFrame(() => requestAnimationFrame(resolve))
            })
            if (!this.awaitingConfig) {
              this.hasInitialFit = true
            }
          } else {
            await this.applyAutoFramingForCurrentScene({ instant: false })
          }
        }
      } catch (err) {
        console.error('FrameRenderer: failed to build scene', err)
      } finally {
        if (!silent && !this.awaitingConfig && version === this.rebuildVersion) {
          this.loading = false
          // The camera fit has finished and options are loaded
          this.showThumbnail = false
        }
      }
    },

    /**
     * Update scene geometry scales and positions for new dimensions.
     * No geometry creation - GPU-friendly, just transforms.
     */
    updateSceneDimensions(artW, artH, sourceOverride) {
      if (!this.builtDims) return
      const b = this.builtDims
      const mw = this.parsedMatWidth
      const innerW = artW + mw.left + mw.right
      const innerH = artH + mw.top + mw.bottom

      if (this.artworkMesh) {
        this.artworkMesh.scale.set(
          artW / (b.planeW || b.artW),
          artH / (b.planeH || b.artH),
          1
        )
        const tex = this.artworkMesh.material?.map
        if (tex) {
          const imgAspect = this._getImageAspect()
          const s = sourceOverride || {
            scale: this.parsedArtworkScale,
            offsetLeft: this.parsedArtworkOffsetLeft,
            offsetTop: this.parsedArtworkOffsetTop,
          }
          const leg = getCropFromScale(artW, artH, s.scale, s.offsetLeft, s.offsetTop, imgAspect)
          applyArtworkTextureCropWindow(tex, artW, artH, leg.fullWidth, leg.fullHeight, leg.cropLeft, leg.cropTop)
        }
        const alphaMap = this.artworkMesh.material?.alphaMap
        if (alphaMap) {
          const rx = artW * 4
          const ry = artH * 4
          alphaMap.repeat.set(rx, ry)
          alphaMap.offset.set(0.5 * (1 - rx), 0.5 * (1 - ry))
        }
      }

      if (this.matGroup) {
        if (this.matGroup.userData.multi) {
          // Re-run createMultiMatBorder layout in-place so border widths stay fixed
          // while only the artwork opening grows/shrinks (group scale would stretch borders).
          const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
          const widthsArr = this.matWidthsArray || []
          if (matArr.length >= 2 && widthsArr.length) {
            const reveal = inches(0.25)
            const d = inches(0.0625)
            let innerW = inches(artW)
            let innerH = inches(artH)
            let outerW = innerW
            let outerH = innerH
            for (let idx = matArr.length - 1; idx >= 0; idx--) {
              const w = widthsArr[idx] || { top: 2, bottom: 2, left: 2, right: 2 }
              const mt = inches(w.top); const mb = inches(w.bottom)
              const ml = inches(w.left); const mr = inches(w.right)
              if (idx < matArr.length - 1) { innerW = outerW; innerH = outerH }
              const xr = idx > 0 ? reveal : 0
              const fullW = innerW + ml + mr + 2 * xr
              const fullH = innerH + mt + mb + 2 * xr
              const cx = (mr - ml) / 2
              const matNum = idx + 1
              const newSizes = {
                top:    { w: fullW,       h: mt + xr,           x: cx,                              y:  innerH / 2 + (mt + xr) / 2 },
                bottom: { w: fullW,       h: mb + xr,           x: cx,                              y: -(innerH / 2 + (mb + xr) / 2) },
                left:   { w: ml + xr,     h: innerH + 2 * xr,   x: -(innerW / 2 + (ml + xr) / 2),  y: 0 },
                right:  { w: mr + xr,     h: innerH + 2 * xr,   x:  innerW / 2 + (mr + xr) / 2,    y: 0 },
              }
              this.matGroup.children.forEach((mesh) => {
                if (mesh.userData?.type === 'mat-piece' && mesh.userData.matNum === matNum) {
                  const s = newSizes[mesh.userData.side]
                  if (s && mesh.geometry && s.w > 0 && s.h > 0) {
                    mesh.geometry.dispose()
                    mesh.geometry = new THREE.BoxGeometry(s.w, s.h, d)
                    mesh.position.set(s.x, s.y, mesh.position.z)
                  }
                }
              })
              outerW = fullW; outerH = fullH
            }
          }
        } else {
          const fullW = artW + mw.left + mw.right
          const cx = (mw.right - mw.left) / 2
          const d = inches(0.0625)
          const aw = inches(artW)
          const ah = inches(artH)
          const byLabel = {
            'Mat (top)': { scale: [inches(fullW), inches(mw.top), 1], pos: [inches(cx), ah / 2 + inches(mw.top) / 2, d / 2] },
            'Mat (bottom)': { scale: [inches(fullW), inches(mw.bottom), 1], pos: [inches(cx), -(ah / 2 + inches(mw.bottom) / 2), d / 2] },
            'Mat (left)': { scale: [inches(mw.left), ah, 1], pos: [-(aw / 2 + inches(mw.left) / 2), 0, d / 2] },
            'Mat (right)': { scale: [inches(mw.right), ah, 1], pos: [aw / 2 + inches(mw.right) / 2, 0, d / 2] },
          }
          this.matGroup.traverse((obj) => {
            if (obj.geometry?.parameters && obj.userData?.type === 'mat-piece') {
              const p = obj.geometry.parameters
              const cfg = byLabel[obj.userData.label]
              if (cfg) {
                obj.scale.set(cfg.scale[0] / p.width, cfg.scale[1] / p.height, cfg.scale[2] / p.depth)
                obj.position.set(...cfg.pos)
              }
            }
          })
        }
      }

      if (this.frameGroup) {
        const frameInnerW = b.isFloatFrame ? innerW + 2 * (b.floatFrameGap || 0.25) : innerW
        const frameInnerH = b.isFloatFrame ? innerH + 2 * (b.floatFrameGap || 0.25) : innerH
        const fw = b.isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const frameDepthVal = b.frameDepthUsed ?? Math.min(2, fw)
        const d = inches(frameDepthVal) / 2
        const iw = inches(frameInnerW)
        const ih = inches(frameInnerH)
        const offsetX = inches((mw.right - mw.left) / 2)
        const offsetY = inches((mw.top - mw.bottom) / 2)
        const frameZ = b.isCanvas ? -inches(frameDepthVal) : 0
        this.frameGroup.position.set(offsetX, offsetY, frameZ)

        this.frameGroup.traverse((obj) => {
          if (obj.userData?.type === 'frame-rail') {
            const pos = obj.userData.position
            if (pos === 'top' || pos === 'bottom') {
              updateMiteredRailGeometry(obj, frameInnerW, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set(0, (pos === 'top' ? 1 : -1) * ih / 2, d)
              obj.scale.y = pos === 'bottom' ? -1 : 1
            } else {
              updateMiteredRailGeometry(obj, frameInnerH, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set((pos === 'right' ? 1 : -1) * iw / 2, 0, d)
            }
          } else if (b.isFloatFrame && obj.userData?.type === 'frame-back-leg') {
            updateBackLegGeometry(obj, frameInnerW, frameInnerH, fw, frameDepthVal)
          }
        })

        this.builtDims.innerW = innerW
        this.builtDims.innerH = innerH
      }

      if (this.stretcherGroup) {
        this.stretcherGroup.scale.set(artW / b.artW, artH / b.artH, 1)
      }

      if (this.substrateBackMesh) {
        const sb = b.substrateBorder || 0
        if (sb > 0) {
          const denomW = b.artW + 2 * sb
          const denomH = b.artH + 2 * sb
          this.substrateBackMesh.scale.set((artW + 2 * sb) / denomW, (artH + 2 * sb) / denomH, 1)
        } else {
          this.substrateBackMesh.scale.set(artW / b.artW, artH / b.artH, 1)
        }
        const sbOffsetX = inches((mw.right - mw.left) / 2)
        const sbOffsetY = inches((mw.top - mw.bottom) / 2)
        this.substrateBackMesh.position.set(sbOffsetX, sbOffsetY, 0)
      }

      if (this.hangHardwareGroup && b.hwBuiltW != null && b.hwBuiltH != null) {
        const scaleX = (b.hasStretchers ? artW : innerW + 2) / (b.hasStretchers ? b.hwBuiltW : b.backPanelBuiltW)
        const scaleY = (b.hasStretchers ? artH : innerH + 2) / (b.hasStretchers ? b.hwBuiltH : b.backPanelBuiltH)
        this.hangHardwareGroup.scale.set(scaleX, scaleY, 1)
        const hwOffsetX = inches((mw.right - mw.left) / 2)
        const hwOffsetY = inches((mw.top - mw.bottom) / 2)
        this.hangHardwareGroup.position.set(hwOffsetX, hwOffsetY, 0)
      }

      if (this.handlesGroup) {
        const aw = inches(artW)
        const ah = inches(artH)
        const s = sourceOverride || {
          scale: this.parsedArtworkScale,
          offsetLeft: this.parsedArtworkOffsetLeft,
          offsetTop: this.parsedArtworkOffsetTop,
        }
        const corners = [
          { x: -aw / 2, y: ah / 2 },
          { x: aw / 2, y: ah / 2 },
          { x: -aw / 2, y: -ah / 2 },
          { x: aw / 2, y: -ah / 2 },
        ]
        const imgCenterX = inches(s.offsetLeft)
        const imgCenterY = -inches(s.offsetTop)
        const scaleOffset = aw * 0.25 * Math.max(1, s.scale)
        let i = 0
        this.handlesGroup.traverse((obj) => {
          if (obj.userData?.type === 'resize-handle') {
            obj.position.set(corners[i].x, corners[i].y, 0.5)
            i++
          } else if (obj.userData?.type === 'source-handles') {
            obj.position.set(imgCenterX, imgCenterY, 0)
          } else if (obj.userData?.type === 'source-handle-move') {
            obj.position.set(0, 0, 0.6)
          } else if (obj.userData?.type === 'source-handle-scale') {
            obj.position.set(scaleOffset, 0, 0.6)
          } else if (obj.userData?.type === 'source-handle-link') {
            const handleSize = inches(1) // radius (2" diameter)
            const linkLen = Math.abs(scaleOffset)
            if (obj.geometry && obj.geometry.parameters) {
              const p = obj.geometry.parameters
              obj.scale.set(linkLen / p.width, (handleSize * 0.25) / p.height, 0.1 / p.depth)
            }
            obj.position.set(scaleOffset / 2, 0, 0.55)
          }
        })
      }
    },

    /**
     * Legacy fit camera behavior: always fit the full framed piece with padding.
     * Retained as a compatibility fallback.
     */
    fitCameraLegacy() {
      if (!this.threeCamera || !this.threeRenderer) return
      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const container = this.$refs.container
      if (!container) return
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400
      const aspect = containerW / containerH

      const sceneW = inches(outerWidth)
      const sceneH = inches(outerHeight)

      // Compute distance for telephoto perspective camera
      const fov = this.threeCamera.fov
      const dist = computeFitDistance(sceneW, sceneH, fov, aspect, 1.15)
      const targetZ = dist + this.frontZ

      this.threeCamera.aspect = aspect
      this.threeCamera.position.set(0, EYE_LEVEL, targetZ)
      this.threeCamera.zoom = 1
      this.threeCamera.lookAt(0, EYE_LEVEL, 0)
      this.threeCamera.updateProjectionMatrix()

      this.threeRenderer.setSize(containerW, containerH)
    },

    getOuterSceneSize() {
      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })
      return {
        sceneW: inches(outerWidth),
        sceneH: inches(outerHeight),
      }
    },

    getRequiredCameraModeForVisibleArtwork() {
      if (this.cameraMode === CAMERA_MODE_FREE) return CAMERA_MODE_FREE

      const size = this.getOuterSceneSize()
      if (!size) return this.cameraMode || CAMERA_MODE_STANDARD

      return this._selectAutoFramingMode(size.sceneW, size.sceneH)
    },

    async applyAutoFramingForCurrentScene({ instant = false } = {}) {
      if (!this.threeCamera || !this.threeRenderer) return
      if (this.cameraMode === CAMERA_MODE_FREE) return

      const requiredMode = this.getRequiredCameraModeForVisibleArtwork()
      if (requiredMode === CAMERA_MODE_FREE) return

      const currentMode = this.cameraMode || CAMERA_MODE_STANDARD
      const shouldPromote = shouldPromoteCameraMode(currentMode, requiredMode)

      if ((this.cameraMode == null || shouldPromote) && requiredMode !== this.cameraMode) {
        this.$emit('request-camera-mode', requiredMode)
        await this.$nextTick()
      }

      if (instant) {
        this.fitCamera()
        return
      }

      await this.animateFitCamera()
    },

    getSizingSceneSize() {
      const targetW = parseFloat(this.targetWidth)
      const targetH = parseFloat(this.targetHeight)
      if (Number.isFinite(targetW) && targetW > 0 && Number.isFinite(targetH) && targetH > 0) {
        return {
          sceneW: inches(targetW),
          sceneH: inches(targetH),
        }
      }
      return this.getOuterSceneSize()
    },

    getContainerMetrics() {
      const container = this.$refs.container
      if (!container) return null
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400

      const canonicalAspect = CANONICAL_RENDER_ASPECT
      let renderW
      let renderH

      // Cover-fit render viewport: always fill container and allow crop overflow.
      if ((containerW / containerH) >= canonicalAspect) {
        renderW = containerW
        renderH = containerW / canonicalAspect
      } else {
        renderH = containerH
        renderW = containerH * canonicalAspect
      }

      return {
        containerW,
        containerH,
        containerAspect: containerW / containerH,
        renderW,
        renderH,
        renderAspect: canonicalAspect,
      }
    },

    computeCameraTarget(mode = this.cameraMode) {
      const containerMetrics = this.getContainerMetrics()
      if (!containerMetrics) return null

      const { sceneW, sceneH } = this.getSizingSceneSize()
      const { containerW, containerH, renderW, renderH, renderAspect } = containerMetrics
      const fov = this.threeCamera?.fov || 75

      // Parent `cameraMode` starts null; thumbnail + first fit use same auto pick as `request-camera-mode`.
      const effectiveMode = (mode == null)
        ? this._selectAutoFramingMode(sceneW, sceneH)
        : mode

      const legacyDist = computeFitDistance(sceneW, sceneH, fov, renderAspect, AUTO_FRAMING_PIECE_PADDING)

      const baselineH = STANDARD_REFERENCE_VIEWPORT_HEIGHT
      const heightScale = renderH > 0 ? (renderH / baselineH) : 1

      const preset = FRAMING_PRESET_BY_MODE.get(effectiveMode) ?? AUTO_FRAMING_PRESETS[0]
      const chosenBase = autoFramingPresetDistance(preset, fov, renderAspect)
      const modeDist = chosenBase * heightScale
      const dist = Number.isFinite(modeDist) && modeDist > 0 ? modeDist : legacyDist
      const targetZ = dist + this.frontZ

      return {
        aspect: renderAspect,
        containerW,
        containerH,
        renderW,
        renderH,
        targetZ,
      }
    },

    getDisplaySizeForTarget(target, sceneSizeOverride = null) {
      if (!target) return null
      const size = sceneSizeOverride || this.getSizingSceneSize()
      const { sceneW, sceneH } = size || {}
      if (!Number.isFinite(sceneW) || !Number.isFinite(sceneH) || sceneW <= 0 || sceneH <= 0) {
        return null
      }

      const { renderH } = target
      const fov = this.threeCamera?.fov || 75
      const fitDist = target.targetZ - this.frontZ

      const viewHeight = 2 * fitDist * Math.tan((THREE.MathUtils.degToRad(fov)) / 2)
      if (!Number.isFinite(viewHeight) || viewHeight <= 0) return null

      const pxPerUnit = renderH / viewHeight
      const width = Math.max(1, Math.round(sceneW * pxPerUnit))
      const height = Math.max(1, Math.round(sceneH * pxPerUnit))

      return { width, height }
    },

    getThumbnailDisplaySize() {
      const target = this.computeCameraTarget(this.cameraMode)
      return this.getDisplaySizeForTarget(target)
    },

    fitCamera() {
      if (!this.threeCamera || !this.threeRenderer) return

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) {
        this.fitCameraLegacy()
        return
      }

      this.threeCamera.aspect = target.aspect
      this.threeCamera.position.set(0, EYE_LEVEL, target.targetZ)
      this.threeCamera.lookAt(0, EYE_LEVEL, 0)
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setSize(target.renderW, target.renderH)

    },

    onThumbnailImageLoad(event) {
      const image = event?.target
      const naturalWidth = Number(image?.naturalWidth)
      const naturalHeight = Number(image?.naturalHeight)
      if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight)) return
      if (naturalWidth <= 0 || naturalHeight <= 0) return

      if (this.threeRenderer && !this.awaitingConfig) {
        this.fitCamera()
      }
      if (this.thumbnailTransitionName) {
        this.$emit('transition-thumbnail-ready')
      }
    },

    /** Animate camera distance from current to fitted over duration ms. No-ops if already at target. */
    animateFitCamera(duration = 1000) {
      if (!this.threeCamera || !this.threeRenderer) return Promise.resolve()

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return Promise.resolve()
      const targetX = 0
      const targetY = EYE_LEVEL
      const targetZ = target.targetZ
      const startX = this.threeCamera.position.x
      const startY = this.threeCamera.position.y
      const startZ = this.threeCamera.position.z
      const startQuat = this.threeCamera.quaternion.clone()

      // Compute the target orientation (perpendicular, centered) without changing the live camera.
      this.threeCamera.position.set(targetX, targetY, targetZ)
      this.threeCamera.lookAt(0, EYE_LEVEL, 0)
      this.threeCamera.updateMatrixWorld()
      const targetQuat = this.threeCamera.quaternion.clone()
      this.threeCamera.position.set(startX, startY, startZ)
      this.threeCamera.quaternion.copy(startQuat)
      this.threeCamera.updateMatrixWorld()

      this.threeCamera.aspect = target.aspect
      this.threeRenderer.setSize(target.renderW, target.renderH)

      // Skip animation if already close to target pose.
      const posEps = Math.max(0.001, targetZ * 0.01)
      if (
        Math.abs(targetX - startX) < posEps &&
        Math.abs(targetY - startY) < posEps &&
        Math.abs(targetZ - startZ) < posEps
      ) {
        return Promise.resolve()
      }

      return new Promise((resolve) => {
        const startTime = performance.now()

        const animate = (now) => {
          const t = Math.min((now - startTime) / duration, 1)
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

          this.threeCamera.position.x = startX + (targetX - startX) * eased
          this.threeCamera.position.y = startY + (targetY - startY) * eased
          this.threeCamera.position.z = startZ + (targetZ - startZ) * eased
          this.threeCamera.quaternion.copy(startQuat).slerp(targetQuat, eased)
          this.threeCamera.updateProjectionMatrix()

          if (t < 1) {
            requestAnimationFrame(animate)
          } else {
            resolve()
          }
        }
        requestAnimationFrame(animate)
      })
    },

    // ------------------------------------------------------------------
    // Render loop
    // ------------------------------------------------------------------

    startRenderLoop() {
      let lastTime = performance.now()
      const render = (now) => {
        this.animFrameId = requestAnimationFrame(render)
        if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return

        const deltaSeconds = Math.max(0, Math.min((now - lastTime) / 1000, 0.25))
        lastTime = now

        if (this._freeLookActive) {
          this._updateFreeLookMovement(deltaSeconds)
        }

        if (this.sceneContentGroup) {
          this.sceneContentGroup.rotation.y = this.flipRotation
          this.sceneContentGroup.position.z = this.flipZOffset || 0
        }

        this._updateDecorEntranceAnimation(now)

        this.threeRenderer.render(this.threeScene, this.threeCamera)
      }
      render(performance.now())
    },

    stopRenderLoop() {
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId)
        this.animFrameId = null
      }
    },

    /**
     * Animate flip to show back of frame (180° Y rotation).
     * Two-phase: first detach from wall (translate +Z), then rotate.
     */
    flipToBack() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const detachPhase = 0.35
      const startRotation = this.flipRotation
      const startZ = this.flipZOffset
      let clearance = 0
      if (this.wallEnabled && this.builtDims) {
        const { outerWidth, outerHeight } = computeOuterDimensions({
          artWidth: this.builtDims.artW,
          artHeight: this.builtDims.artH,
          matTop: this.builtDims.mw?.top ?? 0,
          matBottom: this.builtDims.mw?.bottom ?? 0,
          matLeft: this.builtDims.mw?.left ?? 0,
          matRight: this.builtDims.mw?.right ?? 0,
          frameWidth: this.frameWidth,
          substrateBorder: this.builtDims.substrateBorder,
        })
        const halfW = inches(outerWidth) / 2
        const halfH = inches(outerHeight) / 2
        const backDepth = this.builtDims.backDepth ?? 2
        const halfExtent = Math.max(halfW, halfH)
        clearance = Math.sqrt(halfExtent * halfExtent + backDepth * backDepth) - backDepth + 1
      }
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)

        if (t <= detachPhase) {
          const p = t / detachPhase
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipZOffset = startZ + clearance * ep
          this.flipRotation = startRotation
        } else {
          this.flipZOffset = startZ + clearance
          const p = (t - detachPhase) / (1 - detachPhase)
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipRotation = startRotation + (Math.PI - startRotation) * ep
        }

        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: true })
        }
      }
      requestAnimationFrame(animate)
    },

    /**
     * Animate flip to show front of frame (0° Y rotation).
     * Two-phase: first rotate back, then reattach to wall (translate -Z).
     */
    flipToFront() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const rotatePhase = 0.65
      const startRotation = this.flipRotation
      const startZ = this.flipZOffset
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)

        if (t <= rotatePhase) {
          const p = t / rotatePhase
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipRotation = startRotation + (0 - startRotation) * ep
          this.flipZOffset = startZ
        } else {
          this.flipRotation = 0
          const p = (t - rotatePhase) / (1 - rotatePhase)
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipZOffset = startZ * (1 - ep)
        }

        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: false })
        }
      }
      requestAnimationFrame(animate)
    },

    /** Toggle between front and back view. */
    toggleFlip() {
      const showingBack = this.flipRotation > Math.PI / 2
      if (showingBack) this.flipToFront()
      else this.flipToBack()
    },

    // ------------------------------------------------------------------
    // Interaction handlers
    // ------------------------------------------------------------------

    getMouseNDC(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )
    },

    onMouseMove(event) {
      if (!this.interactive || !this.threeRaycaster || !this.threeCamera) return

      const ndc = this.getMouseNDC(event)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)

      // Dragging is handled by document mousemove (tracks beyond canvas)
      if (this.isDragging) return

      // Collect all meshes for raycasting
      const showingBack = this.flipRotation > Math.PI / 2
      const frontHandleTypes = new Set([
        'resize-handle', 'source-handle-move', 'source-handle-scale', 'source-handle-link',
      ])
      const objects = []
      this.threeScene.traverse((obj) => {
        if (obj.isMesh && obj.userData.type) {
          if (showingBack && frontHandleTypes.has(obj.userData.type)) return
          objects.push(obj)
        }
      })

      const intersects = this.threeRaycaster.intersectObjects(objects)

      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
      }

      if (intersects.length > 0) {
        // In flip/back view, front handles are excluded from raycasting entirely.
        const hit = (!showingBack
            && intersects.find(i => frontHandleTypes.has(i.object.userData?.type))?.object)
          || intersects[0].object
        this.hoveredObject = hit
        this.onHover(hit, event)
        this.updateHandlesVisibility(hit)
      } else {
        this.tooltipVisible = false
        this.showDimensions = false
        this.$refs.container.style.cursor = 'default'
        this.hoveredObject = null
        this.updateHandlesVisibility(null)
      }
    },

    onHover(mesh, event) {
      const data = mesh.userData
      if (!data) return

      const rect = this.$refs.container.getBoundingClientRect()
      this.tooltipX = event.clientX - rect.left + 12
      this.tooltipY = event.clientY - rect.top - 8

      if (data.type === 'resize-handle') {
        this.$refs.container.style.cursor = this.getCursorForCorner(data.corner)
        mesh.material.opacity = 0.95
        this.tooltipText = 'Drag to resize'
        this.tooltipVisible = true
        return
      }

      this.$refs.container.style.cursor = 'default'

      if (data.type === 'artwork') {
        this.tooltipText = `${this.parsedArtWidth}" × ${this.parsedArtHeight}"`
        this.tooltipVisible = true
        this.showDimensionLabels()
      } else if (data.type === 'frame-rail') {
        this.tooltipText = `Frame: ${data.widthInches}" wide`
        this.tooltipVisible = true
      } else if (data.type === 'mat-piece') {
        const num = data.matNum != null && data.matNum !== '' ? data.matNum : null
        const side = data.side || data.label || 'border'
        const w = data.widthInches != null && !Number.isNaN(Number(data.widthInches))
          ? Number(data.widthInches)
          : null
        this.tooltipText = (num != null && w != null)
          ? `Mat ${num}: ${w}" ${side}`
          : (w != null ? `Mat: ${w}" ${side}` : `Mat ${side}`)
        this.tooltipVisible = true
      } else {
        this.tooltipVisible = false
      }
    },

    onUnhover() {
      // Handle visibility is managed by updateHandlesVisibility
    },

    /** Show/hide resize handles based on hover state. Handles visible only when hovering framed image. */
    updateHandlesVisibility(hoveredMesh) {
      if (!this.handlesGroup) return
      const showingBack = this.flipRotation > Math.PI / 2
      const show = !showingBack && (this.isDragging || (hoveredMesh && (
        hoveredMesh.userData?.type === 'artwork' ||
        hoveredMesh.userData?.type === 'frame-rail' ||
        hoveredMesh.userData?.type === 'mat-piece' ||
        hoveredMesh.userData?.type === 'resize-handle' ||
        hoveredMesh.userData?.type === 'source-handle-move' ||
        hoveredMesh.userData?.type === 'source-handle-scale'
      )))
      const baseOpacity = show ? 0.6 : 0
      const isHandleType = (t) =>
        t === 'resize-handle' || t === 'source-handle-move' || t === 'source-handle-scale' || t === 'source-handle-link'
      const hoveredHandle = show && hoveredMesh && isHandleType(hoveredMesh.userData?.type) ? hoveredMesh : null
      this.handlesGroup.traverse((obj) => {
        if (obj.isMesh && isHandleType(obj.userData?.type)) {
          obj.material.opacity = (obj === hoveredHandle) ? 0.95 : baseOpacity
        }
      })
    },

    showDimensionLabels() {
      if (!this.threeCamera || !this.threeRenderer) return

      this.showDimensions = true
      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })
      this.widthLabel = `${outerWidth}"`
      this.heightLabel = `${outerHeight}"`

      const outerW = inches(outerWidth)
      const outerH = inches(outerHeight)
      const labelOffset = 0.5

      // Project 3D points to screen (position labels at outer edges of framed piece)
      const widthMid = new THREE.Vector3(0, -outerH / 2 - labelOffset, 0)
      const heightMid = new THREE.Vector3(outerW / 2 + labelOffset, 0, 0)

      const rect = this.$refs.container.getBoundingClientRect()
      const w = rect.width
      const h = rect.height

      widthMid.project(this.threeCamera)
      this.widthLabelPos = {
        x: (widthMid.x * 0.5 + 0.5) * w,
        y: (-widthMid.y * 0.5 + 0.5) * h,
      }

      heightMid.project(this.threeCamera)
      this.heightLabelPos = {
        x: (heightMid.x * 0.5 + 0.5) * w,
        y: (-heightMid.y * 0.5 + 0.5) * h,
      }
    },

    getCursorForCorner(corner) {
      const cursors = {
        'top-left': 'nwse-resize',
        'top-right': 'nesw-resize',
        'bottom-left': 'nesw-resize',
        'bottom-right': 'nwse-resize',
      }
      return cursors[corner] || 'pointer'
    },

    onMouseDown(event) {
      if (!this.interactive || !this.hoveredObject) return
      const data = this.hoveredObject.userData || {}

      if (data.type === 'resize-handle') {
        this._beginDrag(event, 'resize', this.getCursorForCorner(data.corner))
        this.dragHandle = data.corner
        this.dragStartDims = { width: this.parsedArtWidth, height: this.parsedArtHeight }
        return
      }

      if (data.type === 'source-handle-move' || data.type === 'source-handle-scale') {
        const mode = data.type === 'source-handle-move' ? 'source-move' : 'source-scale'
        this._beginDrag(event, mode, mode === 'source-move' ? 'move' : 'ew-resize')
        this._showSourceGhost()
      }
    },

    onMouseUp() {
      this.endResizeDrag()
    },

    endResizeDrag() {
      if (!this.isDragging) return
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.isDragging = false
      this.dragMode = null
      this.dragHandle = null
      this.dragStartPos = null
      this.dragStartDims = null
      this.dragWidth = null
      this.dragHeight = null
      // Flush any pending RAF (dragSourceStart must stay alive for this).
      if (this.dragRafId != null) {
        cancelAnimationFrame(this.dragRafId)
        this.applyPendingDragDims()
      }
      if (this.sourceDragRafId != null) {
        cancelAnimationFrame(this.sourceDragRafId)
        this.applyPendingSourceTransform()
      }
      this._hideSourceGhost()
      this.dragSourceStart = null
      this.lastEmittedDims = null
      this.debouncedRebuild()
    },

    onMouseLeave() {
      this.tooltipVisible = false
      this.showDimensions = false
      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
        this.hoveredObject = null
      }
      this.updateHandlesVisibility(null)
      if (!this.isDragging) this.$refs.container.style.cursor = 'default'
    },

    handleDrag(event) {
      if (!this.dragStartPos) return

      const dx = event.clientX - this.dragStartPos.x
      const dy = event.clientY - this.dragStartPos.y

      // Convert pixel delta to inch delta (use canvas rect to match NDC/raycasting)
      const canvas = this.$refs.canvas
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      // PerspectiveCamera: derive scene-units-per-pixel from FOV + camera distance to z=0
      const camDist = this.threeCamera.position.z
      const fovRad = THREE.MathUtils.degToRad(this.threeCamera.fov)
      const sceneH = 2 * camDist * Math.tan(fovRad / 2)
      const sceneW = sceneH * this.threeCamera.aspect
      const pxToInchX = (sceneW / rect.width)
      const pxToInchY = (sceneH / rect.height)

      const dxInches = dx * pxToInchX
      const dyInches = dy * pxToInchY
      if (this.dragMode === 'resize') {
        if (!this.dragStartDims) return

        let newWidth = this.dragStartDims.width
        let newHeight = this.dragStartDims.height

        const corner = this.dragHandle
        const resizeFromCenterFactor = 2

        if (corner === 'top-right' || corner === 'bottom-right') {
          newWidth += dxInches * resizeFromCenterFactor
        } else {
          newWidth -= dxInches * resizeFromCenterFactor
        }
        if (corner === 'bottom-left' || corner === 'bottom-right') {
          newHeight += dyInches * resizeFromCenterFactor
        } else {
          newHeight -= dyInches * resizeFromCenterFactor
        }

        newWidth = Math.max(1, Math.round(newWidth * 4) / 4)
        newHeight = Math.max(1, Math.round(newHeight * 4) / 4)

        this.dragWidth = newWidth
        this.dragHeight = newHeight
        if (this.dimGroup) this.dimGroup.visible = false

        // RAF-throttle: batch mousemoves into one update per frame (avoids 4 ExtrudeGeometry allocs per mousemove)
        this.pendingDragDims = { width: newWidth, height: newHeight }
        if (this.dragRafId == null) {
          this.dragRafId = requestAnimationFrame(() => this.applyPendingDragDims())
        }
      } else if (this.dragMode === 'source-move') {
        this.handleSourceMoveDrag(dxInches, dyInches)
      } else if (this.dragMode === 'source-scale') {
        this.handleSourceScaleDrag(dxInches)
      }
    },

    applyPendingDragDims() {
      this.dragRafId = null
      const d = this.pendingDragDims
      this.pendingDragDims = null
      if (!d) return

      const sourceAdj = this.adjustSourceForResize(d.width, d.height)
      this.updateSceneDimensions(d.width, d.height, sourceAdj)

      if (!this.lastEmittedDims || this.lastEmittedDims.width !== d.width || this.lastEmittedDims.height !== d.height) {
        this.lastEmittedDims = d
        this.$emit('resize', { width: d.width, height: d.height })
      }
      if (sourceAdj) {
        this.$emit('source-transform', sourceAdj)
      }
    },

    // ------------------------------------------------------------------
    // Resize handling
    // ------------------------------------------------------------------

    onResize() {
      if (this._freeLookActive) return
      this.applyAutoFramingForCurrentScene({ instant: true })
    },

    // ------------------------------------------------------------------
    // Free-look controls
    // ------------------------------------------------------------------

    enableFreeLook() {
      if (!this._pointerControls || this._freeLookActive) return
      this._freeLookActive = true
      this._freeLookKeys.clear()
      document.addEventListener('keydown', this._boundFreeLookKeyDown)
      document.addEventListener('keyup', this._boundFreeLookKeyUp)
      this._pointerControls.addEventListener('unlock', this._boundPointerUnlock)
      this._pointerControls.lock()
      this.$emit('enter-free-look')
    },

    disableFreeLook() {
      if (!this._freeLookActive) return
      this._freeLookActive = false
      this._freeLookKeys.clear()
      document.removeEventListener('keydown', this._boundFreeLookKeyDown)
      document.removeEventListener('keyup', this._boundFreeLookKeyUp)
      if (this._pointerControls) {
        this._pointerControls.removeEventListener('unlock', this._boundPointerUnlock)
        this._pointerControls.unlock()
      }
      this.$emit('exit-free-look')
    },

    _onFreeLookKeyDown(event) {
      const key = event.key.toLowerCase()
      if (!['w', 'a', 's', 'd'].includes(key)) return
      this._freeLookKeys.add(key)
    },

    _onFreeLookKeyUp(event) {
      const key = event.key.toLowerCase()
      if (!['w', 'a', 's', 'd'].includes(key)) return
      this._freeLookKeys.delete(key)
    },

    _onPointerUnlock() {
      this.disableFreeLook()
    },

    _updateFreeLookMovement(deltaSeconds = 1 / 60) {
      if (!this._freeLookActive || !this._pointerControls) return
      const speedInchesPerSecond = 180 // ~15 ft/s
      const distance = speedInchesPerSecond * deltaSeconds

      if (this._freeLookKeys.has('w')) {
        this._pointerControls.moveForward(distance)
      }
      if (this._freeLookKeys.has('s')) {
        this._pointerControls.moveForward(-distance)
      }
      if (this._freeLookKeys.has('a')) {
        this._pointerControls.moveRight(-distance)
      }
      if (this._freeLookKeys.has('d')) {
        this._pointerControls.moveRight(distance)
      }
    },

    // ------------------------------------------------------------------
    // Export
    // ------------------------------------------------------------------

    /**
     * Export a uniform thumbnail of the 3D canvas.
     * Uses the same aspect and perpendicular framing as live view, but may pull the camera
     * farther back so the full outer frame fits (live view can clip when standard scale is tight).
     * @param {number} [maxDim=800] - Maximum dimension in pixels
     * @returns {string} data URL
     */
    exportThumbnail(maxDim = 800) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return null
      const exportAspect = target.aspect

      const outerSize = this.getOuterSceneSize()
      const { sceneW: outerW, sceneH: outerH } = outerSize || {}
      const fov = this.threeCamera.fov
      const minZForFullOuterFrame =
        Number.isFinite(outerW) && outerW > 0 && Number.isFinite(outerH) && outerH > 0
          ? computeFitDistance(outerW, outerH, fov, exportAspect, THUMBNAIL_EXPORT_OUTER_PADDING) + this.frontZ
          : target.targetZ
      const exportTargetZ = Math.max(target.targetZ, minZForFullOuterFrame)

      // Render at a fixed 4x internal resolution, then downscale the cropped
      // result back to ~maxDim (keeps thumbnails sharp).
      const renderScale = 4
      let thumbW, thumbH
      if (exportAspect > 1) {
        thumbW = maxDim * renderScale
        thumbH = Math.round((maxDim * renderScale) / exportAspect)
      } else {
        thumbH = maxDim * renderScale
        thumbW = Math.round((maxDim * renderScale) * exportAspect)
      }

      // Save current state
      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const origW = rect.width || 400
      const origH = rect.height || 400
      const origAspect = this.threeCamera.aspect
      const origZ = this.threeCamera.position.z
      const origPixelRatio = this.threeRenderer.getPixelRatio()

      // Temporarily adjust camera and renderer for thumbnail
      this.threeCamera.aspect = exportAspect
      this.threeCamera.position.z = exportTargetZ
      this.threeCamera.lookAt(0, EYE_LEVEL, 0)
      this.threeCamera.updateProjectionMatrix()

      // Force pixel ratio to 1 so the output is exactly maxDim pixels
      this.threeRenderer.setPixelRatio(1)
      // Use false to prevent CSS layout shifts
      this.threeRenderer.setSize(thumbW, thumbH, false)

      // Ensure rotation is 0 (front view) for thumbnail
      const origRotation = this.sceneContentGroup ? this.sceneContentGroup.rotation.y : 0
      const origZOffset = this.sceneContentGroup ? this.sceneContentGroup.position.z : 0
      if (this.sceneContentGroup) {
        this.sceneContentGroup.rotation.y = 0
        this.sceneContentGroup.position.z = 0
      }

      // Hide decor plant for thumbnail exports (scale reference only; not part of product image).
      const decor = this._decorObject
      const decorWasVisible = decor ? decor.visible !== false : null
      if (decor) decor.visible = false

      try {
        // Render and capture
        this.threeRenderer.render(this.threeScene, this.threeCamera)
      } finally {
        if (decor) decor.visible = decorWasVisible
      }

      // Crop to the projected frame bounds so saved thumbnails don't include
      // excessive viewport background. Use export camera distance so the crop matches
      // the frame extents after pulling back to fit the full outer frame.
      let dataURL
      const displayTarget = {
        renderW: thumbW,
        renderH: thumbH,
        targetZ: exportTargetZ,
        aspect: exportAspect,
      }
      const displaySize = this.getDisplaySizeForTarget(displayTarget, outerSize)
      if (displaySize && thumbW > 0 && thumbH > 0) {
        const projectedW = Math.max(1, Math.round(displaySize.width))
        const projectedH = Math.max(1, Math.round(displaySize.height))

        const cropW = Math.max(1, Math.min(thumbW, projectedW))
        const cropH = Math.max(1, Math.min(thumbH, projectedH))
        const cropX = Math.max(0, Math.round((thumbW - cropW) / 2))
        const cropY = Math.max(0, Math.round((thumbH - cropH) / 2))

        const cropLongest = Math.max(cropW, cropH)
        const downscale = cropLongest > maxDim ? (maxDim / cropLongest) : 1
        const outW = Math.max(1, Math.round(cropW * downscale))
        const outH = Math.max(1, Math.round(cropH * downscale))

        const outCanvas = document.createElement('canvas')
        outCanvas.width = outW
        outCanvas.height = outH
        const outCtx = outCanvas.getContext('2d')
        if (outCtx) {
          outCtx.drawImage(
            this.threeRenderer.domElement,
            cropX,
            cropY,
            cropW,
            cropH,
            0,
            0,
            outW,
            outH
          )
          dataURL = outCanvas.toDataURL('image/jpeg', 0.9)
        }
      }

      if (!dataURL) {
        dataURL = this.threeRenderer.domElement.toDataURL('image/jpeg', 0.9)
      }

      // Restore original state
      if (this.sceneContentGroup) {
        this.sceneContentGroup.rotation.y = origRotation
        this.sceneContentGroup.position.z = origZOffset
      }
      this.threeCamera.aspect = origAspect
      this.threeCamera.position.z = origZ
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setPixelRatio(origPixelRatio)
      this.threeRenderer.setSize(target.renderW || origW, target.renderH || origH, false)

      return dataURL
    },

    /**
     * Export the current render as a PNG data URL.
     * @param {number} [scale=2] - Resolution multiplier
     * @returns {string} data URL
     */
    exportDataURL(scale = 2) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const w = Math.round(rect.width * scale)
      const h = Math.round(rect.height * scale)

      // Temporarily resize renderer for high-res export
      this.threeRenderer.setSize(w, h)
      this.threeRenderer.render(this.threeScene, this.threeCamera)
      const dataURL = this.threeRenderer.domElement.toDataURL('image/png')

      // Restore original size
      this.threeRenderer.setSize(rect.width, rect.height)

      return dataURL
    },

    /**
     * Export as a Blob (for upload or download).
     * @param {number} [scale=2]
     * @returns {Promise<Blob>}
     */
    async exportBlob(scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return null
      const res = await fetch(dataURL)
      return res.blob()
    },

    /**
     * Trigger a browser download of the rendered image.
     * @param {string} [filename='frame-render.png']
     * @param {number} [scale=2]
     */
    downloadImage(filename = 'frame-render.png', scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return
      const link = document.createElement('a')
      link.href = dataURL
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.frame-renderer {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.frame-renderer__canvas-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.frame-renderer canvas {
  display: block;
}

.frame-renderer__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 10;
  transform: translateY(-100%);
}

.frame-renderer__dimensions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.frame-renderer__dim-label {
  position: absolute;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1976D2;
  background: rgba(255,255,255,0.85);
  padding: 1px 5px;
  border-radius: 2px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.frame-renderer__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.frame-renderer__thumbnail {
  display: block;
}
</style>
